import { FC, useMemo } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import sanitize from "sanitize-html";
import { Select } from "../../../../../components/forms/select";
import { TextField } from "../../../../../components/forms/text";
import { Field } from "../../internal/field";
import { Description, FieldHook, RenderFuncProps, Title } from "../common";
import { useAllowedChains, useSchema } from "../source-wallet/common";

const Elem: FC<RenderFuncProps> = ({ item }: RenderFuncProps) => {
  const { control } = useFormContext();
  const allowedChains = useAllowedChains(item);

  const { t, i18n } = useTranslation("fields");
  const currentLanguage = useMemo(() => i18n.language.replaceAll("-", "_"), [
    i18n,
  ]);

  const options = useMemo(
    () => allowedChains.map((value) => ({ title: value, value })),
    [allowedChains]
  );

  const title = useMemo(
    () =>
      Field.isDestinationWalletField(item) && item.title?.[currentLanguage]
        ? item.title[currentLanguage]
        : undefined,
    [item, currentLanguage]
  );

  const description = useMemo(
    () =>
      Field.isDestinationWalletField(item) && item.desc?.[currentLanguage]
        ? sanitize(item.desc[currentLanguage])
        : undefined,
    [item, currentLanguage]
  );

  return (
    <>
      {title && description ? (
        <>
          <Title>{title}</Title>
          <Description
            dangerouslySetInnerHTML={{ __html: description }}
          ></Description>
        </>
      ) : null}
      {title && !description ? (
        <>
          <Description></Description>
          <Title>{title}</Title>
        </>
      ) : null}
      {description && !title ? (
        <>
          <Description></Description>
          <Description
            dangerouslySetInnerHTML={{ __html: description }}
          ></Description>
        </>
      ) : null}
      <div>{t("destination-wallet-chain")}</div>
      <Controller
        control={control}
        name={`${item.field}.chain`}
        render={({ field, fieldState: { error } }) => (
          <Select
            {...field}
            error={!!error}
            helperText={error?.message}
            options={options}
            fullWidth
          />
        )}
      />
      <div>{t("destination-wallet-address")}</div>
      <Controller
        control={control}
        name={`${item.field}.address`}
        render={({ field, fieldState: { error } }) => (
          <TextField
            {...field}
            error={!!error}
            helperText={error?.message}
            fullWidth
          />
        )}
      />
    </>
  );
};

export const useDestinationWalletField: FieldHook = () => {
  const schema = useSchema();
  return useMemo(
    () => ({
      schema,
      elem: Elem,
      cryptoField: true,
    }),
    [schema]
  );
};
